import { useEffect, useMemo, useState } from 'react';
import {
    Box,
    InputAdornment,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import { Search as SearchIcon, MenuBook as MenuBookIcon } from '@mui/icons-material';
import { ThemeProvider } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
    getTopicsByGroup,
    HELP_GROUP_ORDER,
    OVERVIEW_TOPIC_ID,
} from '../domain/help/helpTopics';
import { getTopicIcon } from '../components/help/topicIcons';
import HelpTopicView from '../components/help/HelpTopicView';
import { helpTheme } from '../components/help/helpTheme';
import PageHeaderBanner from '../components/PageHeaderBanner';

/** Documentation Center: searchable index of every help topic plus the reader. */
const HelpCenter = () => {
    const { t } = useTranslation('help');
    const location = useLocation();
    const [selected, setSelected] = useState(OVERVIEW_TOPIC_ID);
    const [search, setSearch] = useState('');

    // Deep-link support: /help#<topicId> selects that topic.
    useEffect(() => {
        const hash = location.hash.replace(/^#/, '');
        if (hash) setSelected(hash);
    }, [location.hash]);

    const query = search.trim().toLowerCase();

    const groups = useMemo(
        () =>
            HELP_GROUP_ORDER.map(group => {
                const topics = getTopicsByGroup(group)
                    .map(topic => ({
                        topic,
                        title: t(`topics.${topic.id}.title`),
                        summary: t(`topics.${topic.id}.summary`),
                    }))
                    .filter(
                        entry =>
                            query === '' ||
                            entry.title.toLowerCase().includes(query) ||
                            entry.summary.toLowerCase().includes(query),
                    );
                return { group, topics };
            }).filter(entry => entry.topics.length > 0),
        [query, t],
    );

    const hasResults = groups.length > 0;

    return (
        <ThemeProvider theme={helpTheme}>
            <Box
                sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}
                bgcolor="background.default"
                color="text.primary"
            >
                <PageHeaderBanner
                    icon={<MenuBookIcon />}
                    title={t('center.title')}
                    subtitle={t('center.subtitle')}
                    accentColor="#0891b2"
                />

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                    <Paper
                        variant="outlined"
                        sx={{
                            width: { xs: '100%', md: 320 },
                            flexShrink: 0,
                            alignSelf: 'flex-start',
                            position: { md: 'sticky' },
                            top: { md: 16 },
                        }}
                    >
                        <Box p={1.5}>
                            <TextField
                                fullWidth
                                size="small"
                                value={search}
                                onChange={event => setSearch(event.target.value)}
                                placeholder={t('center.searchPlaceholder')}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                        </Box>
                        {hasResults ? (
                            <Box sx={{ maxHeight: { md: '70vh' }, overflowY: 'auto' }}>
                                {groups.map(({ group, topics }) => (
                                    <List
                                        key={group}
                                        dense
                                        subheader={
                                            <ListSubheader disableSticky>
                                                {t(`center.groups.${group}`)}
                                            </ListSubheader>
                                        }
                                    >
                                        {topics.map(({ topic, title }) => (
                                            <ListItemButton
                                                key={topic.id}
                                                selected={selected === topic.id}
                                                onClick={() => setSelected(topic.id)}
                                            >
                                                <ListItemIcon sx={{ minWidth: 36 }}>
                                                    {getTopicIcon(topic.iconKey)}
                                                </ListItemIcon>
                                                <ListItemText primary={title} />
                                            </ListItemButton>
                                        ))}
                                    </List>
                                ))}
                            </Box>
                        ) : (
                            <Box p={2}>
                                <Typography variant="body2" color="text.secondary">
                                    {t('center.noResults')}
                                </Typography>
                            </Box>
                        )}
                    </Paper>

                    <Paper variant="outlined" sx={{ flex: 1, p: { xs: 2, md: 4 } }}>
                        <HelpTopicView topicId={selected} />
                    </Paper>
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default HelpCenter;
